import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { UsersManagementUserAvatarComponent } from '../../pages/users-management/users-management-user-avatar.component';
import { TabViewModule } from 'primeng/tabview';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { IonicModule } from '@ionic/angular';
import moment from 'moment';
import { getUserTypeIconPath } from '../../pages/users-management/users-management-user-type';
import { AccountSuspension, suspensionReasonLabelKey } from '../../utils/an-account-suspension';
import {
  getSportIconPath,
  isSportIconUrl,
  resolveActivityImageUrl,
  resolveGroupImageUrl,
} from '../../pages/users-management/users-management-avatar';
import {
  UpdateUserManagementPayload,
  UsersManagementDetailResponse,
  UsersManagementStoreService,
} from '../../../services/users-management/users-management.store.service';
import { ActivitiesManagementApiService } from '../../../services/activities-management/activities-management-api.service';
import { GroupsManagementApiService } from '../../../services/groups-management/groups-management-api.service';
import type { ActivityStatus } from '../../models/activities-management.model';
import type { EditGroupActivity, GroupRow } from '../../models/management.model';
import { aHeadingTakesMoreThanOneLine } from '../../utils/what-one-line-of-a-heading-measures';
export interface Sport {
  sportId: string;
  name: string;
  level: number;
  joinedAt: string;
}

export interface Location {
  lat: number;
  lng: number;
}

export interface Activity {
  id: number;
  title: string;
  schedule: string;
  role: string;
  status: 'Cancelled' | 'Completed' | 'Ongoing' | 'Upcoming';
  image?: string;
  location?: string;
  venue?: string;
  type?: string;
  sportKey?: string;
  distance?: string;
  level?: string;
}

export interface Group {
  id: number;
  title: string;
  image?: string;
  location?: string;
  sport?: string;
  membersCount?: number;
  subgroupsCount?: number;
  type?: string;
  sportKey?: string;
}

export interface UserTypeInfo {
  key: string;
  label: string;
  isValid: boolean;
  insuranceExpirationDate?: string;
}

export interface UserDetail {
  id: string | number;
  name: string;
  email: string;
  avatar: string;
  hasProfile?: boolean;
  userType?: UserTypeInfo;
  role?: string;
  status: 'Active' | 'Inactive';
  activities: number;
  sportsCount: number;
  notification: boolean;
  /**
   * Stored notification preferences, one key per notification, per channel.
   * A key that is absent means on (the API reads the same opt-out model). Only
   * present on the detail payload.
   */
  notifications?: { mail: Record<string, boolean>; mobile: Record<string, boolean> };
  sports?: Sport[];
  location?: Location;
  firstName?: string;
  lastName?: string;
  /**
   * City declared on the profile. Optional here because the generated SDK does
   * not describe it yet: the API returns it, the SDK types are regenerated from
   * a running instance. Drop the optionality at the next regeneration.
   */
  city?: string;
  /**
   * Profile completion percentage, 0-100. Same SDK caveat as city above.
   */
  profileCompletion?: number;
  /**
   * The same count the member is shown, from the same declared list.
   *
   * A percentage alone was the old shape and it came from a different
   * calculation: an admin saw one number and the member another for the same
   * profile. These three come from the list the application reads.
   */
  profileCompletionMet?: number;
  profileCompletionTotal?: number;
  profileCompletionCriteria?: { key: string; met: boolean }[];
  /**
   * How warm this person is, from what they have done. Optional because the
   * API only computes it on the list: absent means not computed, which is not
   * the same as never having acted.
   */
  temperature?: 'hot' | 'warm' | 'cool' | 'frozen' | 'never';
  phone?: string;
  level?: number;
  createdAt?: string;
  pastActivities?: Activity[];
  ongoingActivities?: Activity[];
  upcomingActivities?: Activity[];
  createdGroups?: Group[];
  joinedGroups?: Group[];
  lastLoginDate?: string;
  lastLoginAt?: string;
  /** Present while the account is suspended and waits for its deletion. */
  suspension?: AccountSuspension;
}

/** One switch on the consent panel. `keys` is one or more preference keys that
 *  move together, exactly as the mobile app groups them. */
export interface ConsentLine {
  labelKey: string;
  keys: string[];
}

export interface ConsentGroup {
  titleKey: string;
  lines: ConsentLine[];
}

/**
 * The notification preferences, mirrored from the mobile app's own settings
 * screens (profile-notifs-push-page / profile-notifs-mail-page) so the
 * back-office offers exactly the granularity a member has. Grouped multi-key
 * lines (group life, my role) toggle all their keys together, like the app.
 */
export const CONSENT_PUSH_GROUPS: ConsentGroup[] = [
  {
    titleKey: 'userDetail.consent.push.activities',
    lines: [
      { labelKey: 'userDetail.consent.keys.event_edit_or_cancel', keys: ['event_edit_or_cancel'] },
      { labelKey: 'userDetail.consent.keys.event_new_participant', keys: ['event_new_participant'] },
      { labelKey: 'userDetail.consent.keys.event_reminder', keys: ['event_reminder'] },
      { labelKey: 'userDetail.consent.keys.event_share', keys: ['event_share'] },
      { labelKey: 'userDetail.consent.keys.event_new_event_proximity', keys: ['event_new_event_proximity'] },
    ],
  },
  {
    titleKey: 'userDetail.consent.push.groups',
    lines: [
      { labelKey: 'userDetail.consent.keys.group_invitation', keys: ['group_invitation'] },
      { labelKey: 'userDetail.consent.keys.group_accept', keys: ['group_accept'] },
      { labelKey: 'userDetail.consent.keys.group_new_event', keys: ['group_new_event'] },
      { labelKey: 'userDetail.consent.keys.group_new_member', keys: ['group_new_member'] },
      {
        labelKey: 'userDetail.consent.keys.group_life',
        keys: ['group_request_refused', 'group_member_kicked', 'group_member_left', 'group_invitation_answer'],
      },
      {
        labelKey: 'userDetail.consent.keys.group_my_role',
        keys: ['group_admin_change', 'group_ownership_transferred'],
      },
    ],
  },
  {
    titleKey: 'userDetail.consent.push.chat',
    lines: [
      { labelKey: 'userDetail.consent.keys.chat_new_message_smatcher', keys: ['chat_new_message_smatcher'] },
      { labelKey: 'userDetail.consent.keys.chat_new_message_event', keys: ['chat_new_message_event'] },
      { labelKey: 'userDetail.consent.keys.chat_new_message_group', keys: ['chat_new_message_group'] },
    ],
  },
  {
    titleKey: 'userDetail.consent.push.profile',
    lines: [{ labelKey: 'userDetail.consent.keys.profile_new_follower', keys: ['profile_new_follower'] }],
  },
  {
    titleKey: 'userDetail.consent.others',
    lines: [{ labelKey: 'userDetail.consent.keys.marketing', keys: ['marketing'] }],
  },
];

export const CONSENT_MAIL_GROUPS: ConsentGroup[] = [
  {
    titleKey: 'userDetail.consent.mail.activities',
    lines: [
      { labelKey: 'userDetail.consent.keys.event_confirm_register', keys: ['event_confirm_register'] },
      { labelKey: 'userDetail.consent.keys.event_confirm_unregister', keys: ['event_confirm_unregister'] },
      { labelKey: 'userDetail.consent.keys.event_edit_or_cancel', keys: ['event_edit_or_cancel'] },
      { labelKey: 'userDetail.consent.keys.event_reminder', keys: ['event_reminder'] },
      { labelKey: 'userDetail.consent.keys.event_share', keys: ['event_share'] },
      { labelKey: 'userDetail.consent.keys.event_participant_unregister', keys: ['event_participant_unregister'] },
    ],
  },
  {
    titleKey: 'userDetail.consent.mail.groups',
    lines: [
      { labelKey: 'userDetail.consent.keys.group_invitation', keys: ['group_invitation'] },
      { labelKey: 'userDetail.consent.keys.group_accept', keys: ['group_accept'] },
      { labelKey: 'userDetail.consent.keys.group_new_member', keys: ['group_new_member'] },
    ],
  },
  {
    titleKey: 'userDetail.consent.others',
    lines: [{ labelKey: 'userDetail.consent.keys.marketing', keys: ['marketing'] }],
  },
];

@Component({
  selector: 'bo-user-detail',
  templateUrl: './users-management-detail.component.html',
  styleUrl: './users-management-detail.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    CommonModule,
    FormsModule,
    UsersManagementUserAvatarComponent,
    TabViewModule,
    TagModule,
    ButtonModule,
    DividerModule,
    DropdownModule,
    InputTextModule,
    IonicModule,
  ],
})
export class UsersManagementDetailComponent implements OnDestroy {
  #store = inject(UsersManagementStoreService);
  #activitiesApi = inject(ActivitiesManagementApiService);
  #groupsApi = inject(GroupsManagementApiService);

  private readonly SECTION_PAGE_SIZE = 10;
  private readonly INITIAL_VISIBLE_ACTIVITIES = 3;
  private readonly INITIAL_VISIBLE_GROUPS = 2;

  loadedActivities: Activity[] = [];
  activitiesTotal = 0;
  activitiesPage = 1;
  activitiesLoading = false;
  activityTabTotals: Record<number, number> = { 0: 0, 1: 0, 2: 0 };

  loadedGroups: Group[] = [];
  groupsTotal = 0;
  groupsPage = 1;
  groupsLoading = false;
  groupTabTotals: Record<number, number> = { 0: 0, 1: 0 };

  getUserTypeIconPath = getUserTypeIconPath;
  getSportIconPath = getSportIconPath;
  isSportIconUrl = isSportIconUrl;

  formatCreatedOn(value?: string): string | null {
    if (!value) {
      return null;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('MMM D, YYYY') : null;
  }

  formatLastLoginOn(value?: string): string | null {
    if (!value) {
      return null;
    }
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('DD/MM/YYYY H:mm') : null;
  }

  onActivityImageError(event: Event, activity?: Activity): void {
    if (!activity) return;
    activity.image = getSportIconPath(activity.sportKey);
    this.cdr.markForCheck();
  }

  onGroupImageError(event: Event, group?: Group): void {
    if (!group) return;
    group.image = getSportIconPath(group.sportKey);
    this.cdr.markForCheck();
  }

  @Input() user: UserDetail | null = null;
  @Input() visible = false;
  @Input() nestedManagementDrawerOpen = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() editUser = new EventEmitter<UserDetail>();
  @Output() deleteUserRequested = new EventEmitter<UserDetail>();
  @Output() liftSuspensionRequested = new EventEmitter<UserDetail>();
  @Output() activityOpen = new EventEmitter<Activity>();
  @Output() groupOpen = new EventEmitter<Group>();
  @Input() initialMode: 'view' | 'edit' | 'delete' = 'view';
  @Input() allowDelete = true;
  activeActivityTab = 0;
  activeGroupTab = 0;
  showAllActivities = false;
  showAllGroups = false;
  isEditing = false;
  editForm: Partial<UserDetail> = {};
  touchedFields: Set<string> = new Set();
  private _personalOriginal: Partial<UserDetail> | null = null;

  private _sportsOriginal: Sport[] | null = null;
  private _drawerOriginalUser: UserDetail | null = null;
  private readonly EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  private readonly PHONE_REGEX = /^(\+?\d{1,3}[\s-]?)?(\(?\d{2,4}\)?[\s-]?)?\d{3,4}[\s-]?\d{3,4}$/;

  isEditingSports = false;

  mapUrl: SafeResourceUrl | null = null;

  constructor(private cdr: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  isNameWrapped = false;
  private resizeObserver?: ResizeObserver;
  private heading?: ElementRef<HTMLElement>;
  private mapCoordsKey = '';

  /*
   * THE HEADING COMES AND GOES, and that is the whole of this.
   *
   * It sits inside `*ngIf="user"`, so it is absent while the drawer waits for
   * somebody to be picked, and absent again once the drawer is emptied. The
   * measurement used to be set up in ngAfterViewInit, which runs ONCE, when
   * the drawer component is created and no member is chosen yet: reading
   * `.nativeElement` off an undefined query threw on every single open, twice
   * over -- straight away, and again from the deferred first measurement
   * (JAVASCRIPT-ANGULAR-2 and -3, 5 October 2026, production).
   *
   * A setter is told each time the element enters or leaves the view, which is
   * exactly when the observation has to start and stop. One observer for the
   * life of the component, pointed at whichever heading is currently there.
   */
  @ViewChild('nameRef') set nameRef(heading: ElementRef<HTMLElement> | undefined) {
    this.heading = heading;
    this.resizeObserver?.disconnect();
    if (!heading) {
      return;
    }
    this.resizeObserver ??= new ResizeObserver(() => this.checkNameWrap());
    this.resizeObserver.observe(heading.nativeElement);
    // Deferred, as it has always been: the first measurement must not land in
    // the same pass that reported the element, because the template reads
    // `isNameWrapped`.
    setTimeout(() => this.checkNameWrap());
  }
  getFieldError(field: string): string | null {
    if (field === 'phone') {
      return this.getPhoneFormatError();
    }

    return this.getPersonalFieldError(field);
  }

  private getPhoneFormatError(): string | null {
    if (!this.isEditing) return null;
    const value = this.editForm.phone?.toString().trim() ?? '';
    if (!value) return null;
    if (!this.PHONE_REGEX.test(value)) return 'Invalid phone format';
    return null;
  }
  ngOnDestroy() {
    this.resizeObserver?.disconnect();
  }

  /*
   * THE MEASUREMENT HAD NEVER SAID YES ONCE (DEV-278).
   *
   * It read `parseInt(getComputedStyle(el).lineHeight)`, and the heading
   * declares no line-height, so the computed style answers the keyword
   * `normal`. `parseInt('normal')` is NaN, and every comparison with NaN is
   * false: `isNameWrapped` stayed false for every name that has ever existed,
   * and the 80px the stylesheet reserves for the role badge was never applied.
   *
   * The height of a line is now worked out where it can be tested, including
   * the case the drawer is actually in -- nothing declared.
   *
   * `markForCheck` is not optional here. The component is OnPush and a
   * ResizeObserver callback is not something Angular watches, so a correctly
   * measured class would still never reach the DOM. It is called only when the
   * answer CHANGED: a resize fires freely, and asking for a check each time
   * would be asking for one on every pixel of a drag.
   */
  private checkNameWrap() {
    // The heading can be gone by the time a deferred measurement or a resize
    // callback arrives: the drawer is emptied, the element leaves the view.
    const el = this.heading?.nativeElement;
    if (!el) {
      return;
    }
    const wrapped = aHeadingTakesMoreThanOneLine(getComputedStyle(el), el.scrollHeight);
    if (wrapped === this.isNameWrapped) {
      return;
    }
    this.isNameWrapped = wrapped;
    this.cdr.markForCheck();
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes['user']) {
      this.updateMapUrl();
      const userChanged = changes['user'].currentValue !== changes['user'].previousValue;
      if (userChanged && this.isEditing && this.user) {
        this.syncEditFormFromUser();
      }
      if (userChanged && this.visible && this.user) {
        this.bootstrapActivityAndGroupSections();
      }
    }
    if (changes['visible']?.currentValue === true) {
      this.isEditingSports = false;
      this._sportsOriginal = null;
      this.showAllGroups = false;
      this.showAllActivities = false;
      if (this.initialMode !== 'edit') {
        this.isEditing = false;
        this.editForm = {};
        this._personalOriginal = null;
        this.touchedFields.clear();
      }
      const userChanged = changes['user']?.currentValue !== changes['user'].previousValue;
      if (!this._drawerOriginalUser || userChanged) {
        this._drawerOriginalUser = this.user ? this.cloneUser(this.user) : null;
      }
      this.updateMapUrl();

      if (this.initialMode === 'edit') {
        this.enterFullEditMode();
      } else if (this.initialMode === 'delete') {
        this.openDeleteDialog();
      }

      if (this.user) {
        this.bootstrapActivityAndGroupSections();
      }
    }
    if (changes['user'] && !changes['visible']) {
      this._drawerOriginalUser = this.user ? this.cloneUser(this.user) : null;
    }
  }

  get personalRequiredFields(): Array<keyof UserDetail> {
    if (this.user?.hasProfile === false) {
      return ['email'];
    }
    return ['firstName', 'lastName', 'email'];
  }

  get hasRequiredError(): boolean {
    return (
      this.personalRequiredFields.some((f) => !!this.getPersonalFieldError(f as string)) ||
      this.hasIncompleteProfileNameError
    );
  }

  get hasPersonalFormError(): boolean {
    return this.hasRequiredError || !!this.getPhoneFormatError();
  }

  get hasIncompleteProfileNameError(): boolean {
    if (this.user?.hasProfile !== false || !this.isEditing) return false;
    const firstName = this.editForm.firstName?.trim() ?? '';
    const lastName = this.editForm.lastName?.trim() ?? '';
    return (!!firstName && !lastName) || (!firstName && !!lastName);
  }

  private getPersonalFieldError(field: string): string | null {
    if (!this.isEditing) return null;
    if (!this.touchedFields.has(field)) return null;

    const value = this.editForm[field as keyof UserDetail]?.toString().trim() ?? '';
    const isRequired = this.personalRequiredFields.includes(field as keyof UserDetail);

    if (!value) {
      return isRequired ? 'This field is required' : null;
    }

    if (field === 'email' && !this.EMAIL_REGEX.test(value)) {
      return 'Invalid email format';
    }

    return null;
  }

  onEdit() {
    if (!this.user) return;
    this.syncEditFormFromUser();
    this.isEditing = true;
    this.cdr.markForCheck();
  }

  enterFullEditMode() {
    this.onEdit();
    if (!this.isEditingSports) {
      this.startEditSports();
    }
    this.cdr.markForCheck();
  }

  private syncEditFormFromUser() {
    if (!this.user) return;
    this.editForm = { ...this.user };
    this._personalOriginal = { ...this.editForm };
  }

  onFieldInput(fieldName: string) {
    const value = this.editForm[fieldName as keyof UserDetail];
    if (!value?.toString().trim()) {
      this.touchedFields.add(fieldName);
    }
    this.cdr.markForCheck();
  }
  onFieldBlur(fieldName: string) {
    this.touchedFields.add(fieldName);
  }

  onSave() {
    if (!this.user) return;
    this.personalRequiredFields.forEach((f) => this.touchedFields.add(f));
    if (this.getPhoneFormatError()) {
      this.touchedFields.add('phone');
    }
    if (this.hasPersonalFormError) return;
    this.applyPersonalEditFormToUser();
    this.touchedFields.clear();
    this.isEditing = false;
    this._personalOriginal = null;
    this.editUser.emit({ ...this.user });
    this.cdr.markForCheck();
  }

  private applyPersonalEditFormToUser() {
    if (!this.user) return;
    this.user.firstName = this.editForm.firstName?.trim() ?? '';
    this.user.lastName = this.editForm.lastName?.trim() ?? '';
    this.user.email = this.editForm.email?.trim() ?? '';
    this.user.phone = this.editForm.phone?.trim() ?? '';
    const fullName = `${this.user.firstName} ${this.user.lastName}`.trim();
    this.user.name = fullName || this.user.email;
  }

  onDiscard() {
    this.touchedFields.clear();
    this.editForm = {};
    this.isEditing = false;
    this._personalOriginal = null;
    this.cdr.markForCheck();
  }

  incrementSportLevel(index: number) {
    if (!this.user?.sports) return;
    const sport = this.user.sports[index];
    if (sport.level < 10) sport.level++;
  }

  decrementSportLevel(index: number) {
    if (!this.user?.sports) return;
    const sport = this.user.sports[index];
    if (sport.level > 1) sport.level--;
  }

  private updateMapUrl(): void {
    const location = this.user?.location;
    if (!location) {
      this.mapCoordsKey = '';
      this.mapUrl = null;
      return;
    }
    const coordsKey = `${location.lat},${location.lng}`;
    if (coordsKey === this.mapCoordsKey && this.mapUrl) {
      return;
    }
    this.mapCoordsKey = coordsKey;
    const url = `https://maps.google.com/maps?q=${coordsKey}&z=13&output=embed`;
    this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  close() {
    if (this.unsavedChanges) {
      this.discardAll(false);
    } else if (this.anyEditing) {
      this.exitEditModesQuietly();
    }
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onOverlayClick() {
    if (this.nestedManagementDrawerOpen) {
      return;
    }

    this.close();
  }

  get anyEditing(): boolean {
    return this.isEditing || this.isEditingSports;
  }

  get unsavedChanges(): boolean {
    if (this.isEditing) {
      const keys: Array<keyof UserDetail> = ['firstName', 'lastName', 'phone', 'email'];
      for (const k of keys) {
        if ((this.editForm[k] ?? '').toString().trim() !== (this._personalOriginal?.[k] ?? '').toString().trim()) {
          return true;
        }
      }
    }

    if (this.isEditingSports) {
      const a = JSON.stringify(this.user?.sports ?? []);
      const b = JSON.stringify(this._sportsOriginal ?? []);
      if (a !== b) return true;
    }

    return false;
  }

  saveAll() {
    if (!this.user) return;

    const savePersonal = this.isEditing;
    const saveSports = this.isEditingSports;

    if (savePersonal) {
      this.personalRequiredFields.forEach((f) => this.touchedFields.add(f));
      if (this.getPhoneFormatError()) {
        this.touchedFields.add('phone');
      }
      if (this.hasPersonalFormError) {
        this.showToast('Please fix the errors in personal information', 'error');
        this.cdr.markForCheck();
        return;
      }
      this.applyPersonalEditFormToUser();
    }
    if (saveSports) {
      this._sportsOriginal = null;
      this.isEditingSports = false;
    }

    const userId = String(this.user.id);
    const payload = this.buildUpdatePayload({ savePersonal, saveSports });

    if (Object.keys(payload).length === 0) {
      this.showToast('No changes to save', 'info');
      return;
    }

    this.saving = true;
    this.#store.updateUser(userId, payload).subscribe({
      next: (res: UsersManagementDetailResponse) => {
        this.saving = false;
        if (res?.user) {
          Object.assign(this.user!, this.#store.normalizeUser(res.user));
          const fullName = `${this.user!.firstName ?? ''} ${this.user!.lastName ?? ''}`.trim();
          if (fullName) {
            this.user!.name = fullName;
          }
          this._drawerOriginalUser = this.cloneUser(this.user!);
        }
        if (savePersonal) {
          this.isEditing = false;
          this._personalOriginal = null;
          this.touchedFields.clear();
        }
        this.exitEditModesQuietly();
        this.editUser.emit({ ...this.user! });

        if (res?.warnings?.length) {
          this.showToast(res.warnings.join(' '), 'info');
        } else {
          this.showToast('Your changes to the user have been saved', 'success');
        }
        this.cdr.markForCheck();
      },
      error: (err: { error?: { message?: string | string[] } | string }) => {
        this.saving = false;
        const raw =
          (typeof err?.error === 'object' && err.error?.message) ||
          (typeof err?.error === 'string' ? err.error : 'Failed to save user changes');
        const message = Array.isArray(raw) ? raw.join(', ') : raw;
        this.showToast(message, 'error');
        this.cdr.markForCheck();
      },
    });
  }

  saving = false;

  private buildUpdatePayload(flags: { savePersonal: boolean; saveSports: boolean }) {
    if (!this.user) return {};

    const payload: UpdateUserManagementPayload = {};

    if (flags.savePersonal) {
      const source = this.isEditing ? this.editForm : this.user;
      payload.email = source.email?.trim() ?? '';

      const phone = source.phone?.trim() ?? '';
      const originalPhone = (this._personalOriginal?.phone ?? '').toString().trim();
      if (phone !== originalPhone) {
        payload.phone = phone;
      }

      const firstName = source.firstName?.trim() ?? '';
      const lastName = source.lastName?.trim() ?? '';

      if (this.user.hasProfile !== false) {
        payload.firstName = firstName;
        payload.lastName = lastName;
      } else if (firstName && lastName) {
        payload.firstName = firstName;
        payload.lastName = lastName;
      }
    }

    if (flags.saveSports) {
      payload.sportLevels = (this.user.sports ?? [])
        .filter((s) => !!s.sportId?.trim())
        .map((s) => ({
          sportId: s.sportId,
          level: s.level,
        }));
    }

    return payload;
  }

  discardAll(showSuccessToast = true) {
    if (this._drawerOriginalUser && this.user) {
      this.restoreUser(this._drawerOriginalUser, this.user);
    }
    if (this.isEditing) this.onDiscard();
    if (this.isEditingSports) this.discardSports();

    this._drawerOriginalUser = this.user ? this.cloneUser(this.user) : null;

    if (showSuccessToast) {
      this.showToast('All changes to the user have been discarded', 'success');
    }
    this.cdr.markForCheck();
  }

  private exitEditModesQuietly() {
    if (this.isEditing) this.onDiscard();
    if (this.isEditingSports) this.discardSports();
    this.cdr.markForCheck();
  }

  private cloneUser(user: UserDetail): UserDetail {
    return {
      ...user,
      sports: user.sports ? user.sports.map((s) => ({ ...s })) : undefined,
    };
  }

  private restoreUser(source: UserDetail, target: UserDetail) {
    (Object.keys(source) as (keyof UserDetail)[]).forEach((key) => {
      this.setValue(target, key, source[key]);
    });

    this.cdr.markForCheck();
  }

  private setValue<K extends keyof UserDetail>(target: UserDetail, key: K, value: UserDetail[K]) {
    if (Array.isArray(value)) {
      target[key] = [...value] as UserDetail[K];
    } else {
      target[key] = value;
    }
  }
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'info' | 'error' = 'info';
  private _toastTimer: ReturnType<typeof setTimeout> | null = null;

  showToast(message: string, type: 'success' | 'info' | 'error' = 'info', timeout = 4000) {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      this.toastVisible = false;
      this._toastTimer = null;
      this.cdr.markForCheck();
    }, timeout);
    this.cdr.markForCheck();
  }

  hideToast() {
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this.toastVisible = false;
    this._toastTimer = null;
    this.cdr.markForCheck();
  }
  readonly suspensionReasonLabelKey = suspensionReasonLabelKey;

  liftSuspension() {
    if (!this.user?.suspension) return;
    this.liftSuspensionRequested.emit(this.user);
  }

  openDeleteDialog() {
    if (!this.user) return;
    this.deleteUserRequested.emit(this.user);
  }
  getStatusClass(status: string): string {
    return status === 'Active' ? 'status--active' : 'status--inactive';
  }

  getActivityStatusClass(status: string): string {
    const map: Record<string, string> = {
      Cancelled: 'act-badge--cancelled',
      Completed: 'act-badge--completed',
      Ongoing: 'act-badge--ongoing',
      Upcoming: 'act-badge--upcoming',
    };
    return map[status] ?? '';
  }

  getRoleBadgeClass(role: string): string {
    const map: Record<string, string> = {
      Organizer: 'role--pro-organizer',
      Registered: 'role--participant',
    };
    return map[role] ?? '';
  }

  formatActivityLevelLabel(level?: string): string {
    const raw = level?.trim() ?? '';
    if (!raw) {
      return 'All levels';
    }
    const normalized = raw.toLowerCase();
    if (normalized === '0' || normalized === 'all' || normalized === 'all levels') {
      return 'All levels';
    }
    if (normalized.startsWith('level ')) {
      return raw;
    }
    const digits = raw.match(/\d+/)?.[0];
    if (!digits || digits === '0') {
      return 'All levels';
    }
    return `Level ${digits}`;
  }

  get currentActivities(): Activity[] {
    return this.loadedActivities;
  }

  get visibleActivities(): Activity[] {
    return this.showAllActivities
      ? this.currentActivities
      : this.currentActivities.slice(0, this.INITIAL_VISIBLE_ACTIVITIES);
  }

  get currentGroups(): Group[] {
    return this.loadedGroups;
  }

  get visibleGroups(): Group[] {
    const groups = this.currentGroups;
    return this.showAllGroups ? groups : groups.slice(0, this.INITIAL_VISIBLE_GROUPS);
  }

  get groupMoreCount(): number {
    return Math.max(0, this.groupsTotal - this.INITIAL_VISIBLE_GROUPS);
  }

  get activityMoreCount(): number {
    return Math.max(0, this.activitiesTotal - this.INITIAL_VISIBLE_ACTIVITIES);
  }

  onActivityTabChange(tab: number) {
    if (this.activeActivityTab === tab) {
      return;
    }
    this.activeActivityTab = tab;
    this.showAllActivities = false;
    this.resetActivitiesSection();
    this.loadActivities();
  }

  onGroupTabChange(tab: number) {
    if (this.activeGroupTab === tab) {
      return;
    }
    this.activeGroupTab = tab;
    this.showAllGroups = false;
    this.resetGroupsSection();
    this.loadGroups();
  }

  onShowMoreActivities() {
    this.showAllActivities = true;
    if (this.loadedActivities.length >= this.activitiesTotal) {
      this.cdr.markForCheck();
      return;
    }
    this.activitiesPage += 1;
    this.loadActivities(true);
  }

  onShowMoreGroups() {
    this.showAllGroups = true;
    if (this.loadedGroups.length >= this.groupsTotal) {
      this.cdr.markForCheck();
      return;
    }
    this.groupsPage += 1;
    this.loadGroups(true);
  }

  private bootstrapActivityAndGroupSections() {
    if (!this.user || this.anyEditing) {
      return;
    }
    this.resetActivitiesSection();
    this.resetGroupsSection();
    const userId = String(this.user.id);
    this.loadActivityTabTotals(userId);
    this.loadGroupTabTotals(userId);
    this.loadActivities();
    this.loadGroups();
  }

  private resetActivitiesSection() {
    this.loadedActivities = [];
    this.activitiesTotal = 0;
    this.activitiesPage = 1;
    this.showAllActivities = false;
  }

  private resetGroupsSection() {
    this.loadedGroups = [];
    this.groupsTotal = 0;
    this.groupsPage = 1;
    this.showAllGroups = false;
  }

  private loadActivityTabTotals(userId: string) {
    const tabs: Array<{ tab: number; status: ActivityStatus }> = [
      { tab: 0, status: 'incoming' },
      { tab: 1, status: 'completed' },
      { tab: 2, status: 'canceled' },
    ];
    forkJoin(
      tabs.map(({ tab, status }) =>
        this.#activitiesApi
          .list({
            userId,
            status,
            page: 1,
            pageSize: 1,
            sortField: 'date',
            sortOrder: 'desc',
          })
          .pipe(map((res) => ({ tab, total: res.total }))),
      ),
    ).subscribe({
      next: (results) => {
        results.forEach(({ tab, total }) => {
          this.activityTabTotals[tab] = total;
        });
        this.cdr.markForCheck();
      },
    });
  }

  private loadGroupTabTotals(userId: string) {
    forkJoin([
      this.#groupsApi
        .list({
          userId,
          membership: 'created',
          page: 1,
          pageSize: 1,
        })
        .pipe(map((res) => ({ tab: 0, total: res.total }))),
      this.#groupsApi
        .list({
          userId,
          membership: 'joined',
          page: 1,
          pageSize: 1,
        })
        .pipe(map((res) => ({ tab: 1, total: res.total }))),
    ]).subscribe({
      next: (results) => {
        results.forEach(({ tab, total }) => {
          this.groupTabTotals[tab] = total;
        });
        this.cdr.markForCheck();
      },
    });
  }

  private loadActivities(append = false) {
    if (!this.user) {
      return;
    }
    const userId = String(this.user.id);
    if (!append) {
      this.activitiesPage = 1;
      this.loadedActivities = [];
    }
    this.activitiesLoading = true;
    this.#activitiesApi
      .list({
        userId,
        status: this.activityStatusForTab(this.activeActivityTab),
        page: this.activitiesPage,
        pageSize: this.SECTION_PAGE_SIZE,
        sortField: 'date',
        sortOrder: 'desc',
      })
      .subscribe({
        next: (res) => {
          const mapped = res.data.map((activity) => this.mapListActivityToUi(activity, userId));
          this.loadedActivities = append ? [...this.loadedActivities, ...mapped] : mapped;
          this.activitiesTotal = res.total;
          this.activityTabTotals[this.activeActivityTab] = res.total;
          this.activitiesLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.activitiesLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadGroups(append = false) {
    if (!this.user) {
      return;
    }
    const userId = String(this.user.id);
    if (!append) {
      this.groupsPage = 1;
      this.loadedGroups = [];
    }
    this.groupsLoading = true;
    this.#groupsApi
      .list({
        userId,
        membership: this.activeGroupTab === 0 ? 'created' : 'joined',
        page: this.groupsPage,
        pageSize: this.SECTION_PAGE_SIZE,
        sortField: 'name',
        sortOrder: 'asc',
      })
      .subscribe({
        next: (res) => {
          const mapped = res.data.map((group) => this.mapListGroupToUi(group));
          this.loadedGroups = append ? [...this.loadedGroups, ...mapped] : mapped;
          this.groupsTotal = res.total;
          this.groupTabTotals[this.activeGroupTab] = res.total;
          this.groupsLoading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.groupsLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  private activityStatusForTab(tab: number): ActivityStatus {
    if (tab === 1) {
      return 'completed';
    }
    if (tab === 2) {
      return 'canceled';
    }
    return 'incoming';
  }

  private mapListActivityToUi(activity: EditGroupActivity, userId: string): Activity {
    const sportKey = activity.sportIcon ?? activity.sportId;
    return {
      id: activity.id as unknown as number,
      title: activity.name,
      schedule: `${activity.date} • ${activity.time}`,
      role: activity.ownerId === userId ? 'Organizer' : 'Registered',
      status: this.resolveDrawerActivityStatus(activity),
      image: resolveActivityImageUrl(activity.thumbnailUrl, sportKey),
      location: activity.location,
      type: activity.sport,
      sportKey,
      level: activity.level,
    };
  }

  private resolveDrawerActivityStatus(activity: EditGroupActivity): Activity['status'] {
    const apiStatus = activity.status?.trim().toLowerCase() ?? '';
    if (apiStatus === 'cancelled' || apiStatus === 'canceled') {
      return 'Cancelled';
    }
    if (apiStatus === 'finished' || apiStatus === 'completed') {
      return 'Completed';
    }
    const now = moment();
    const start = moment(activity.startDateIso);
    const end = activity.endDateIso ? moment(activity.endDateIso) : start;
    if (start.isValid() && end.isValid() && start.isBefore(now) && end.isAfter(now)) {
      return 'Ongoing';
    }
    return 'Upcoming';
  }

  private mapListGroupToUi(group: GroupRow): Group {
    const sport = typeof group.sport === 'object' && group.sport ? group.sport : null;
    const sportKey = sport?.icon ?? sport?.id ?? '';
    return {
      id: group.id as unknown as number,
      title: group.name,
      image: resolveGroupImageUrl('', sportKey),
      location: group.locationName,
      type: group.sportName ?? sport?.name,
      sportKey,
      membersCount: group.totalMembers ?? group.members,
      subgroupsCount: group.totalSubgroups ?? group.subgroups,
    };
  }

  roleOptions = [
    { label: 'Pro Organizer', value: 'Pro Organizer' },
    { label: 'Organizer', value: 'Organizer' },
    { label: 'Participant', value: 'Participant' },
  ];

  levelOptions = Array.from({ length: 10 }, (_, i) => ({
    label: String(i + 1),
    value: i + 1,
  }));

  get incomingCount(): number {
    return this.activityTabTotals[0] ?? 0;
  }

  get completedCount(): number {
    return this.activityTabTotals[1] ?? 0;
  }

  get cancelledCount(): number {
    return this.activityTabTotals[2] ?? 0;
  }

  startEditSports() {
    if (!this.user) return;
    this._sportsOriginal = this.user.sports ? this.user.sports.map((s) => ({ ...s })) : [];
    this.isEditingSports = true;
  }

  discardSports() {
    if (!this.user) return;
    if (this._sportsOriginal) {
      this.user.sports = this._sportsOriginal.map((s) => ({ ...s }));
    }
    this._sportsOriginal = null;
    this.isEditingSports = false;
    this.cdr.markForCheck();
  }
  getStatusBadgeClass(status: string): string {
    return `status-${status?.toLowerCase()}`;
  }

  openActivityDetail(activity: Activity) {
    this.activityOpen.emit(activity);
  }

  openGroupDetail(group: Group) {
    this.groupOpen.emit(group);
  }

  // --- Notifications & marketing consent panel ---------------------------
  // Mirrors the member's own settings screens. Each line saves on its own,
  // independently of the personal-info edit mode, so it is always reachable
  // (including for a member who never signed in: the save initialises the
  // profile server-side).

  readonly consentPushGroups = CONSENT_PUSH_GROUPS;
  readonly consentMailGroups = CONSENT_MAIL_GROUPS;
  savingConsent = false;

  /** A line reads as on unless one of its keys is explicitly off (opt-out). */
  lineEnabled(channel: 'mail' | 'mobile', keys: string[]): boolean {
    const prefs = this.user?.notifications?.[channel] ?? {};
    return keys.every((key) => prefs[key] !== false);
  }

  toggleLine(channel: 'mail' | 'mobile', keys: string[]): void {
    if (!this.user || this.savingConsent) {
      return;
    }
    const newValue = !this.lineEnabled(channel, keys);
    const patch: Record<string, boolean> = {};
    keys.forEach((key) => (patch[key] = newValue));
    this.saveConsent(channel === 'mail' ? { mail: patch } : { mobile: patch });
  }

  /** The member's real cut-off for a marketing request: email + push at once. */
  cutAllMarketing(): void {
    this.saveConsent({ mail: { marketing: false }, mobile: { marketing: false } });
  }

  cutAllNotifications(): void {
    const mail: Record<string, boolean> = {};
    const mobile: Record<string, boolean> = {};
    CONSENT_MAIL_GROUPS.forEach((group) =>
      group.lines.forEach((line) => line.keys.forEach((key) => (mail[key] = false))),
    );
    CONSENT_PUSH_GROUPS.forEach((group) =>
      group.lines.forEach((line) => line.keys.forEach((key) => (mobile[key] = false))),
    );
    this.saveConsent({ mail, mobile });
  }

  private saveConsent(patch: { mail?: Record<string, boolean>; mobile?: Record<string, boolean> }): void {
    if (!this.user || this.savingConsent) {
      return;
    }
    const userId = String(this.user.id);
    const previous = {
      mail: { ...(this.user.notifications?.mail ?? {}) },
      mobile: { ...(this.user.notifications?.mobile ?? {}) },
    };
    // Optimistic: the switch moves at once, and reverts if the server refuses.
    this.user.notifications = {
      mail: { ...previous.mail, ...(patch.mail ?? {}) },
      mobile: { ...previous.mobile, ...(patch.mobile ?? {}) },
    };
    this.savingConsent = true;
    this.cdr.markForCheck();

    this.#store.updateUser(userId, { notifications: patch }).subscribe({
      next: (res: UsersManagementDetailResponse) => {
        this.savingConsent = false;
        if (res?.user) {
          Object.assign(this.user!, this.#store.normalizeUser(res.user));
          this._drawerOriginalUser = this.cloneUser(this.user!);
        }
        this.editUser.emit({ ...this.user! });
        this.showToast('Notification preferences saved', 'success');
        this.cdr.markForCheck();
      },
      error: () => {
        this.savingConsent = false;
        this.user!.notifications = previous;
        this.showToast('Failed to save notification preferences', 'error');
        this.cdr.markForCheck();
      },
    });
  }
}
